import IBAN from 'iban';
import isValid from 'date-fns/isValid';
import ms from 'ms';
import type { ObjectSchema } from 'yup';
import { addMonths } from 'date-fns';

import Yup, { type YupI18nError } from '@tmh-website/services/validation.service';

import { addBusinessDaysWithGermanHolidays, getPrefillQueryParam, type Theme } from '../electricity-tariff.service';
import {
  ETFormLayout,
  ETFormVariant,
  PaymentMethod,
  Reason,
  Salutation,
  Step,
  Termination,
  Title,
} from '../utils/const';

import { PowerConsumptionShared, ReviewShared, TariffInfoShared, UserDataShared } from './shared';
import type { GetEyondLocationCity } from '../eyond.types';

export const fullAgeDate = new Date(Date.now() - ms('18years'));
export const maxTerminationMonthsInFutureSwitch = 6;
export const minAmountOfDaysTerminationSelf = 11;
export const minAmountOfDaysTerminationTMH = 17;
export const noInterestFor14LawControllableWallbox = '14noInterestControllableWallbox';

export function validateDeliveryDate(deliveryDate: Date, reason: Reason, termination: Termination): boolean {
  if (reason === Reason.Switch && termination === Termination.Self) {
    return true;
  }

  if (!deliveryDate || !isValid(deliveryDate)) {
    throw new Error('Ungültiges Datum');
  }

  if (reason === Reason.Move) {
    if (deliveryDate < new Date(Date.now() - ms('5weeks') - ms('1day'))) {
      throw new Error('Dein Wunschlieferdatum darf maximal 5 Wochen in der Vergangenheit liegen');
    }
  } else {
    if (termination === Termination.TMH) {
      if (deliveryDate < addBusinessDaysWithGermanHolidays(new Date(), minAmountOfDaysTerminationTMH)) {
        throw new Error(
          `Dein Wunschlieferdatum muss mindestens ${minAmountOfDaysTerminationTMH} Werktage in der Zukunft liegen`
        );
      }
    }
  }

  if (deliveryDate > addMonths(new Date(), maxTerminationMonthsInFutureSwitch)) {
    throw new Error('Weiter als 6 Monate in die Zukunft können wir leider keine Verträge anbieten');
  }

  return true;
}

export function getFirstPossibleDateSwitchSelfTermination(): Date {
  const date = addBusinessDaysWithGermanHolidays(new Date(), minAmountOfDaysTerminationSelf);

  date.setHours(0, 0, 0, 0);

  return date;
}

/**
 * Keep validation in sync with server-side validation @ `apps/cms/src/api/electricity-tariff/validation.ts:22`
 */
const PowerConsumption = ({
  isValidLocation,
  getCitySuggestions,
}: {
  getCitySuggestions: (props: GetEyondLocationCity['props']) => Promise<GetEyondLocationCity['response']['success']>;
  isValidLocation: (input: {
    zipCode: string;
    city?: string;
    street?: string;
    streetNumber?: string;
  }) => Promise<boolean>;
}) =>
  Yup.object({
    ...PowerConsumptionShared.fields,
    householdConsumption: Yup.number().min(100).max(100_000).required(),
    householdConsumptionPreset: Yup.mixed(),
    zipCode: Yup.string()
      .trim()
      .length(5)
      .test('isValidLocation', 'Ungültige Adressangaben.', async function (zipCode) {
        if (!zipCode || zipCode.trim().length !== 5) {
          return this.createError({ message: params => ({ key: 'mixed.required', params } as YupI18nError) });
        }

        const response = await getCitySuggestions({
          location: {
            zipCode,
          },
        });

        return (response?.cities ?? []).length > 0;
      }),
    city: Yup.string().required(),
    street: Yup.string().test('isValidLocation', 'Ungültige Adressangaben.', async function (street) {
      const validWithoutAddress = await isValidLocation({
        zipCode: this.parent.zipCode ?? '',
        city: this.parent.city ?? '',
      });

      if (!validWithoutAddress && !(street ?? '')?.trim()) {
        return this.createError({ message: params => ({ key: 'mixed.required', params } as YupI18nError) });
      }

      return isValidLocation({
        zipCode: this.parent.zipCode ?? '',
        city: this.parent.city ?? '',
        street,
        streetNumber: this.parent.streetNumber,
      });
    }),
    streetNumber: Yup.string().test('isValidLocation', 'Ungültige Adressangaben.', async function (streetNumber) {
      const validWithoutAddress = await isValidLocation({
        zipCode: this.parent.zipCode ?? '',
        city: this.parent.city ?? '',
      });
      if (!validWithoutAddress && !(streetNumber ?? '')?.trim()) {
        return this.createError({ message: params => ({ key: 'mixed.required', params } as YupI18nError) });
      }
      return isValidLocation({
        zipCode: this.parent.zipCode ?? '',
        city: this.parent.city ?? '',
        street: this.parent.street ?? '',
        streetNumber,
      });
    }),
    emailDoiPopupConfirmation: Yup.boolean().required(),
  });

export enum SmartMeterConfirmationPopupOption {
  DoesNotHaveSmartMeter = 'doesNotHaveSmartMeter',
  HasSmartMeter = 'hasSmartMeter',
}

const TariffInfo = Yup.object({
  ...TariffInfoShared.fields,
  smartMeterConfirmationPopupSelection: Yup.string().oneOf(Object.values(SmartMeterConfirmationPopupOption)),
});

const UserData = ({
  variant,
  isValidVoucher,
  isValidLocation,
}: {
  variant: ETFormVariant;
  isValidVoucher: (voucher: string) => Promise<boolean> | boolean;
  isValidLocation: (input: {
    zipCode: string;
    city?: string;
    street?: string;
    streetNumber?: string;
  }) => Promise<boolean>;
}) =>
  Yup.object({
    ...UserDataShared.fields,
    phone: Yup.string().when([], {
      is: () => variant === ETFormVariant.RenaultDE,
      then: Yup.string()
        .matches(/^(|\+?[0-9]+)$/, 'Ungültiges Format')
        .required(),
      otherwise: Yup.string().matches(/^(|\+?[0-9]+)$/, 'Ungültiges Format'),
    }),
    salutation: Yup.string().oneOf(Object.values(Salutation)).required(),
    title: Yup.string().oneOf(Object.values(Title)),
    streetNumber: Yup.string().test('isValidLocation', 'Ungültige Adressangaben.', async function (streetNumber) {
      if (!(streetNumber ?? '').trim()) {
        return this.createError({ message: params => ({ key: 'mixed.required', params } as YupI18nError) });
      }

      if (!(this.parent.street ?? '').trim()) {
        // We need the street to validate the street number. If it's not yet provided, we skip the validation.
        return true;
      }

      const contextValues = this.options.context as ValuesDE;
      if (!contextValues) {
        // The context is `undefined` on the server-side.
        return true;
      }

      const isValid = await isValidLocation({
        zipCode: contextValues?.[Step.PowerConsumption]?.zipCode ?? '',
        city: contextValues?.[Step.PowerConsumption]?.city ?? '',
        street: this.parent.street ?? '',
        streetNumber,
      });

      return isValid;
    }),
    billingSalutation: Yup.mixed().when('differentBillingAddress', {
      is: true,
      then: Yup.string().oneOf(Object.values(Salutation)).required(),
    }),
    billingTitle: Yup.string().oneOf(Object.values(Title)),
    billingStreet: Yup.mixed().when('differentBillingAddress', {
      is: true,
      then: Yup.string().max(70).required(),
    }),
    billingStreetNumber: Yup.mixed().when('differentBillingAddress', {
      is: true,
      then: Yup.string().max(10).required(),
    }),
    birthDay: Yup.date().max(fullAgeDate, 'Du musst geschäftsfähig und somit volljährig sein.').required(),
    reason: Yup.string().oneOf(Object.values(Reason)).required(),
    termination: Yup.string().when('reason', {
      is: Reason.Switch,
      then: Yup.string().oneOf(Object.values(Termination)).required(),
    }),
    terminationDate: Yup.mixed().when(['reason', 'termination'], {
      is: (reason: Reason, termination: Termination) => reason === Reason.Switch && termination === Termination.Self,
      then: Yup.date()
        .max(
          addMonths(new Date(), maxTerminationMonthsInFutureSwitch),
          'Weiter als 6 Monate in die Zukunft können wir leider keine Verträge anbieten'
        )
        .required(),
    }),
    currentProvider: Yup.string().when('reason', {
      is: Reason.Switch,
      then: Yup.string().required(),
    }),
    currentProviderCustomerId: Yup.string().max(30),
    desiredDeliveryDate: Yup.mixed().test(function (deliveryDate?: Date) {
      if (!deliveryDate) {
        return this.createError({ message: params => ({ key: 'mixed.required', params } as YupI18nError) });
      }
      try {
        validateDeliveryDate(deliveryDate, this.parent.reason, this.parent.termination);

        return true;
      } catch (e) {
        return this.createError({ message: (e as Error).message });
      }
    }),
    meterNumber: Yup.string().max(20).required(),
    marketLocationId: Yup.string()
      .max(11)
      .matches(/^\d{11}$/, {
        message: 'Deine Marktlokation besteht aus genau 11 numerischen Zeichen',
        excludeEmptyString: true,
      }),
    paymentMethod: Yup.string().oneOf(Object.values(PaymentMethod)).required(),
    iban: Yup.string().when('paymentMethod', {
      is: PaymentMethod.DirectDebit,
      then: Yup.string()
        .trim()
        .required()
        .test('ibanValidation', 'Ungültige IBAN', input =>
          IBAN.isValid(((input ?? '').replace(/\s/g, '') ?? '').trim())
        ),
    }),
    accountHolderName: Yup.string().when('paymentMethod', {
      is: PaymentMethod.DirectDebit,
      then: Yup.string().required().max(70),
    }),
    differentAccountHolder: Yup.mixed().when('paymentMethod', {
      is: PaymentMethod.DirectDebit,
      then: Yup.boolean(),
    }),
    acceptSepaDirectDebit: Yup.mixed().when('paymentMethod', {
      is: PaymentMethod.DirectDebit,
      then: Yup.boolean().isTrue().required(),
    }),
    voucher: Yup.string().test('voucherValidation', 'Ungültiger Aktionscode', async function (input?: string) {
      if (!input || !input.trim()) {
        // An empty voucher is always valid
        return true;
      }

      const voucher = await isValidVoucher(input);

      if (!voucher) {
        return this.createError({ message: 'Ungültiger Aktionscode' });
      }

      return true;
    }),
    // TODO @florianmrz: Maybe we should add oneOf for the e14Law radio buttons
    e14Law: Yup.string(),
  });

const Review = Yup.object({
  ...ReviewShared.fields,
  existingHardware: Yup.array().of(Yup.string().oneOf(['wallbox', 'battery-storage', 'photovoltaic', 'heat-pump'])),
});

const Empty = Yup.object({});

export interface ValuesDE {
  _variant: ETFormVariant.TMH | ETFormVariant.RenaultDE;
  _layout: ETFormLayout.DE;
  [Step.PowerConsumption]: Yup.InferType<ReturnType<typeof PowerConsumption>>;
  [Step.TariffInfo]: Yup.InferType<typeof TariffInfo>;
  [Step.UserData]: Yup.InferType<ReturnType<typeof UserData>>;
  [Step.Review]: Yup.InferType<typeof Review>;
  [Step.Thanks]: Yup.InferType<typeof Empty>;
  [key: string]: unknown;
}

export const initialValues: ValuesDE = {
  _variant: ETFormVariant.TMH,
  _layout: ETFormLayout.DE,
  [Step.PowerConsumption]: {
    zipCode: '',
    city: '',
    street: '',
    streetNumber: '',
    householdConsumption: 2500,
    householdConsumptionPreset: 2500,
    carConsumption: 2700,
    homeConnectionHours: 0,
    calculatorCarConsumptionValues: null,
    emailDoiPopupConfirmation: false,
  },
  [Step.TariffInfo]: {
    tariffId: 0,
    isSmartMeterTariff: false,
    smartMeterConfirmationPopupSelection: undefined,
    tariffPopupConfirmation: false,
  },
  [Step.UserData]: {
    salutation: '',
    title: '',
    firstName: '',
    lastName: '',
    street: '',
    streetNumber: '',
    differentBillingAddress: false,
    billingSalutation: '',
    billingTitle: '',
    billingFirstName: '',
    billingLastName: '',
    billingZipCode: '',
    billingCity: '',
    billingStreet: '',
    billingStreetNumber: '',
    email: '',
    phone: '',
    // @ts-expect-error (we need to set the initial value to an empty string to have the input field displayed as empty)
    birthDay: '',
    reason: '',
    termination: '',
    terminationDate: '',
    currentProvider: '',
    currentProviderCustomerId: '',
    desiredDeliveryDate: '',
    meterNumber: '',
    marketLocationId: '',
    e14Law: '',
    paymentMethod: PaymentMethod.DirectDebit,
    iban: '',
    differentAccountHolder: false,
    accountHolderName: '',
    acceptSepaDirectDebit: false,
    voucher: '',
    personId: '',
  },
  [Step.Review]: {
    acceptAdvertising: false,
    existingHardware: [],
  },
  [Step.Thanks]: {},
};

export function getInitialValues(theme: Theme): ValuesDE {
  (theme.reviewCheckboxEntries ?? []).forEach(entry => {
    initialValues[Step.Review][entry.name] = false;
  });

  const prefill = getPrefillQueryParam();
  if (prefill) {
    initialValues[Step.PowerConsumption].householdConsumption = prefill.householdFormValues.householdConsumption;
    initialValues[Step.PowerConsumption].zipCode = prefill.householdFormValues.zipCode;

    if (prefill.activeForm === 'calculator') {
      initialValues[Step.PowerConsumption].calculatorCarConsumptionValues = prefill.carConsumptionFormValues;

      if (prefill.carConsumptionFormValues.homeConnectionHours) {
        initialValues[Step.PowerConsumption].homeConnectionHours = prefill.carConsumptionFormValues.homeConnectionHours;
      }
    } else {
      initialValues[Step.PowerConsumption].carConsumption = prefill.exactValueFormValues.carConsumption;

      if (prefill.exactValueFormValues.homeConnectionHours) {
        initialValues[Step.PowerConsumption].homeConnectionHours = prefill.exactValueFormValues.homeConnectionHours;
      }
    }
  }

  return initialValues;
}

export function getValidations(
  theme: Theme,
  options: Parameters<typeof PowerConsumption>[0] & Parameters<typeof UserData>[0]
): {
  [key in Step]: ObjectSchema<any>;
} {
  const customValidationReview = {};

  (theme.reviewCheckboxEntries ?? []).forEach(entry => {
    let validation = Yup.boolean();

    if (entry.required) {
      validation = validation.isTrue().required();
    }

    customValidationReview[entry.name] = validation;
  });

  return {
    [Step.PowerConsumption]: PowerConsumption({ ...options }),
    [Step.TariffInfo]: TariffInfo,
    [Step.UserData]: UserData({ ...options, variant: theme.variant as ETFormVariant }),
    [Step.Review]: Review.concat(Yup.object(customValidationReview)),
    [Step.Thanks]: Empty,
  };
}
